import React, { useState, useEffect, useContext } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import moment from 'moment-timezone';
import 'moment/locale/ar';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import MobileNavigation from '../components/MobileNavigation';
import { BUSINESS_TIMEZONE } from '../config/timezoneConfig';

// Configure Moment.js for Arabic with Riyadh timezone
moment.locale('ar');

const BookingCalendar = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [properties, setProperties] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');

  // Mobile navigation state
  const [activeNavItem, setActiveNavItem] = useState('bookings');

  const [currentDate, setCurrentDate] = useState(new Date(2025, 2, 1));

  // Platform color mapping
  const platformColors = {
    'Airbnb': '#ff5a5f',
    'Booking.com': '#003580',
    'أخرى': '#4caf50'
  };

  useEffect(() => {
    // Fetch properties on component mount
    const fetchProperties = async () => {
      try {
        const response = await fetch("/api/properties");
        const data = await response.json();

        // Ensure data is an array
        const propertiesArray = Array.isArray(data) ? data :
                                data.properties ? data.properties :
                                [];

        setProperties(propertiesArray);

        // Generate events from all properties' bookings
        generateBookingEvents(propertiesArray);
      } catch (error) {
        console.error("Error fetching properties:", error);
        setProperties([]); // Set to empty array on error
      }
    };

    fetchProperties();
  }, []);

  // Convert bookings to calendar events (FullCalendar format)
  const generateBookingEvents = (propertiesData) => {
    const allEvents = [];

    propertiesData.forEach(property => {
      if (!property.bookings || !Array.isArray(property.bookings)) return;

      property.bookings.forEach(booking => {
        if (!booking.startDate || !booking.endDate) return;

        const startDate = new Date(booking.startDate);
        const endDate = new Date(booking.endDate);

        // Add a day to end date for proper display (calendar is non-inclusive of end date)
        const displayEndDate = new Date(endDate);
        displayEndDate.setDate(displayEndDate.getDate() + 1);

        const source = booking.source || 'أخرى';
        const hasConflict = checkForConflict(property.bookings, booking);
        const backgroundColor = platformColors[source] || platformColors['أخرى'];

        allEvents.push({
          id: `${property._id}-${startDate}-${endDate}`,
          title: `${property.name} (${source})`,
          start: startDate,
          end: displayEndDate,
          backgroundColor: backgroundColor,
          borderColor: hasConflict ? '#ff0000' : backgroundColor,
          extendedProps: {
            propertyId: property._id,
            propertyName: property.name,
            source: source,
            hasConflict: hasConflict
          }
        });
      });
    });

    setEvents(allEvents);
  };

  // Check if a booking conflicts with others
  const checkForConflict = (allBookings, currentBooking) => {
    const currentStart = new Date(currentBooking.startDate);
    const currentEnd = new Date(currentBooking.endDate);
    
    return allBookings.some(otherBooking => {
      if (otherBooking === currentBooking) return false;
      
      const otherStart = new Date(otherBooking.startDate);
      const otherEnd = new Date(otherBooking.endDate);
      
      return (
        (currentStart < otherEnd && currentEnd > otherStart) ||
        (otherStart < currentEnd && otherEnd > currentStart)
      );
    });
  };

  // Filter events based on selected property and source
  const filteredEvents = events.filter(event => {
    const propertyMatch = selectedProperty === 'all' || event.extendedProps?.propertyId === selectedProperty;
    const sourceMatch = selectedSource === 'all' || event.extendedProps?.source === selectedSource;
    return propertyMatch && sourceMatch;
  });

  // Logout handler — AuthContext owns the session lifecycle; this only navigates
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Navigation back to dashboard
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <>
      <MobileNavigation 
        user={user} 
        activeNavItem={activeNavItem} 
        setActiveNavItem={setActiveNavItem}
        onLogout={handleLogout}
      />
      <div style={{ 
        padding: "20px", 
        direction: "rtl", 
        textAlign: "right",
        fontFamily: "'IBM Plex Arabic', Arial, sans-serif"
      }}>
      {/* Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        marginBottom: "20px"
      }}>
        <h2 style={{ 
          margin: 0, 
          fontSize: "1.5rem", 
          fontWeight: "bold" 
        }}>
          🗓️ تقويم الحجوزات
        </h2>
        <div>
          <button 
            onClick={handleBackToDashboard}
            style={{
              padding: "8px 15px",
              backgroundColor: "#4caf50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              marginLeft: "10px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px"
            }}
          >
            🏠 العودة للوحة التحكم
          </button>
          <button 
            onClick={handleLogout}
            style={{
              padding: "8px 15px",
              backgroundColor: "#f44336",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px"
            }}
          >
            🚪 تسجيل الخروج
          </button>
        </div>
      </div>
      
      {/* Legend */}
      <div style={{ 
        marginBottom: "20px", 
        padding: "15px", 
        backgroundColor: "#f9f9f9", 
        borderRadius: "8px",
        display: "flex",
        flexWrap: "wrap",
        gap: "15px",
        alignItems: "center",
        justifyContent: "center"
      }}>
        {Object.entries(platformColors).map(([platform, color]) => (
          <div 
            key={platform}
            style={{ 
              display: "flex", 
              alignItems: "center",
              gap: "10px"
            }}
          >
            <div style={{ 
              width: "25px", 
              height: "25px", 
              backgroundColor: color, 
              borderRadius: "4px" 
            }}></div>
            <span>{platform}</span>
          </div>
        ))}
        <div 
          style={{ 
            display: "flex", 
            alignItems: "center",
            gap: "10px"
          }}
        >
          <div style={{ 
            width: "25px", 
            height: "25px", 
            backgroundColor: "#ff5a5f", 
            border: "3px solid #ff0000",
            borderRadius: "4px"
          }}></div>
          <span>تعارض في الحجز</span>
        </div>
      </div>
      
      {/* Filters */}
      <div style={{ 
        marginBottom: "20px", 
        display: "flex", 
        flexWrap: "wrap",
        gap: "20px",
        justifyContent: "center"
      }}>
        <div>
          <label style={{ 
            marginLeft: "10px", 
            fontWeight: "bold",
            display: "block",
            marginBottom: "5px"
          }}>
            العقار:
          </label>
          <select 
            value={selectedProperty} 
            onChange={(e) => setSelectedProperty(e.target.value)}
            style={{ 
              padding: "8px", 
              borderRadius: "4px",
              border: "1px solid #ddd",
              width: "250px"
            }}
          >
            <option value="all">جميع العقارات</option>
            {properties && Array.isArray(properties) && properties.map(property => (
              <option key={property._id} value={property._id}>
                {property.name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label style={{ 
            marginLeft: "10px", 
            fontWeight: "bold",
            display: "block",
            marginBottom: "5px"
          }}>
            المصدر:
          </label>
          <select 
            value={selectedSource} 
            onChange={(e) => setSelectedSource(e.target.value)}
            style={{ 
              padding: "8px", 
              borderRadius: "4px",
              border: "1px solid #ddd",
              width: "250px"
            }}
          >
            <option value="all">جميع المصادر</option>
            <option value="Airbnb">Airbnb</option>
            <option value="Booking.com">Booking.com</option>
            <option value="أخرى">أخرى</option>
          </select>
        </div>
      </div>
      
      {/* Calendar */}
      <div style={{
        height: 700,
        marginTop: "20px",
        direction: "rtl"
      }}>
        <FullCalendar
          plugins={[dayGridPlugin, interactionPlugin, momentTimezonePlugin]}
          initialView="dayGridMonth"
          timeZone={BUSINESS_TIMEZONE}
          locale="ar"
          direction="rtl"
          initialDate={currentDate}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: ''
          }}
          buttonText={{
            today: 'اليوم',
            month: 'شهر',
            prev: 'السابق',
            next: 'التالي'
          }}
          events={filteredEvents}
          eventContent={(eventInfo) => {
            const hasConflict = eventInfo.event.extendedProps?.hasConflict;
            return (
              <div style={{
                backgroundColor: eventInfo.event.backgroundColor || '#471396',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '0.85rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                border: hasConflict ? '3px solid #ff0000' : 'none'
              }}>
                {eventInfo.event.title}
              </div>
            );
          }}
          height="100%"
          dayMaxEvents={3}
          moreLinkText={(num) => `+ ${num} حجز إضافي`}
        />
      </div>
    </div>
    </>
  );
};

export default BookingCalendar;
